import React from 'react';
import { Box, Checkbox, FormControlLabel, Tooltip } from '@mui/material';

import { Component, isTruss, MagnitudeUnit } from '@paragon/contract-library';
import {
  distributedForceToleranceInPoundsPerFoot,
  forceToleranceInPounds,
  stressToleranceInPoundsPerSquareFoot,
} from '@paragon/geometry-library';
import { DistanceField, NumericField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { PsfPlfToggle } from '../elements/PsfPlfToggle';
import { manualLoadMagnitudeIsGreaterThanZero } from '../insertions/manualLoadValidation';
import { LoadDistribution } from './LoadDistributionSelect';

interface LoadMagnitudeSettingsProps {
  readonly loadDistribution: LoadDistribution;
  readonly startOrOnlyMagnitude: number;
  readonly endMagnitude: number;
  readonly magnitudeUnit: MagnitudeUnit;
  readonly submitStartOrOnlyMagnitude: (startOrOnlyMagnitude: number) => void;
  readonly submitEndMagnitude: (endMagnitude: number) => void;
  readonly submitMagnitudeUnit: (magnitudeUnit: MagnitudeUnit) => void;
  readonly isProjected?: boolean;
  readonly submitIsProjected?: (isProjected: boolean) => void;
  readonly component: Component;
  readonly canSubmitWhenInvalid?: boolean;
  readonly disabled?: boolean;
}

export function LoadMagnitudeSettings(props: LoadMagnitudeSettingsProps) {
  const {
    component,
    disabled,
    loadDistribution,
    endMagnitude,
    magnitudeUnit,
    startOrOnlyMagnitude,
    submitStartOrOnlyMagnitude,
    submitEndMagnitude,
    submitMagnitudeUnit,
    isProjected,
    submitIsProjected,
    canSubmitWhenInvalid,
  } = props;

  let minimumManualLoadMagnitude: number = 0;
  if (loadDistribution !== 'Trapezoidal') {
    switch (magnitudeUnit) {
      case 'Pounds':
        minimumManualLoadMagnitude = forceToleranceInPounds;
        break;
      case 'PoundsPerLinearFoot':
        minimumManualLoadMagnitude = distributedForceToleranceInPoundsPerFoot;
        break;
      case 'PoundsPerSquareFoot':
        minimumManualLoadMagnitude = stressToleranceInPoundsPerSquareFoot;
        break;
      default:
        assertExhaustiveSwitch(magnitudeUnit);
    }
  }

  const loadStartMagnitudeEntryLabel =
    loadDistribution === 'Concentrated'
      ? 'Load Magnitude'
      : loadDistribution === 'Uniform'
        ? 'Load Magnitude'
        : 'Load Start Magnitude';
  const loadStartMagnitudeEntry = (
    <NumericField
      label={loadStartMagnitudeEntryLabel}
      value={startOrOnlyMagnitude}
      min={minimumManualLoadMagnitude}
      onSubmit={submitStartOrOnlyMagnitude}
      validator={(value) => {
        if (
          !manualLoadMagnitudeIsGreaterThanZero(value, magnitudeUnit) &&
          loadDistribution === 'Trapezoidal' &&
          !manualLoadMagnitudeIsGreaterThanZero(endMagnitude, magnitudeUnit)
        ) {
          return `Start & End <= ${minimumManualLoadMagnitude}`;
        }
      }}
      canSubmitWhenInvalid={canSubmitWhenInvalid}
      endAdornment={magnitudeUnitToEndAdornmentText(magnitudeUnit)}
      fullWidth={false}
      sx={{ m: 1, flex: 1 }}
      disabled={disabled}
    />
  );
  const distributedLoadMagnitudeUnit = magnitudeUnit !== 'Pounds' && (
    <PsfPlfToggle magnitudeUnit={magnitudeUnit} onChange={submitMagnitudeUnit} sx={{ m: 1 }} disabled={disabled} />
  );
  const trussSpacingForReference = isTruss(component) && (
    <DistanceField disabled label="Truss Spacing" value={component.spacing} sx={{ m: 1 }} fullWidth={false} />
  );
  const projectedCheckbox = isProjected != null && submitIsProjected && (
    <Tooltip
      disableInteractive
      title="Apply the magnitude per foot of run perpendicular to the load (e.g. horizontal run for a vertical load) instead of per foot of member length"
    >
      <FormControlLabel
        sx={{ m: 1 }}
        control={<Checkbox checked={isProjected} onChange={(event) => submitIsProjected(event.target.checked)} />}
        label="Projected"
        disabled={disabled}
      />
    </Tooltip>
  );

  if (loadDistribution === 'Uniform') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          {loadStartMagnitudeEntry}
          {distributedLoadMagnitudeUnit}
        </Box>
        <Box sx={{ display: 'flex' }}>
          {trussSpacingForReference}
          {projectedCheckbox}
        </Box>
      </Box>
    );
  }
  if (loadDistribution === 'Trapezoidal') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          {loadStartMagnitudeEntry}
          <NumericField
            label="Load End Magnitude"
            value={endMagnitude}
            min={minimumManualLoadMagnitude}
            onSubmit={submitEndMagnitude}
            validator={(value) => {
              if (
                !manualLoadMagnitudeIsGreaterThanZero(value, magnitudeUnit) &&
                loadDistribution === 'Trapezoidal' &&
                !manualLoadMagnitudeIsGreaterThanZero(startOrOnlyMagnitude, magnitudeUnit)
              ) {
                return `Start & End <= ${minimumManualLoadMagnitude}`;
              }
            }}
            canSubmitWhenInvalid={canSubmitWhenInvalid}
            endAdornment={magnitudeUnitToEndAdornmentText(magnitudeUnit)}
            fullWidth={false}
            sx={{ m: 1, flex: 1 }}
            disabled={disabled}
          />
          {distributedLoadMagnitudeUnit}
        </Box>
        <Box sx={{ display: 'flex' }}>
          {trussSpacingForReference}
          {projectedCheckbox}
        </Box>{' '}
      </Box>
    );
  }
  return loadStartMagnitudeEntry;
}

export function magnitudeUnitToEndAdornmentText(magnitudeUnit: MagnitudeUnit) {
  switch (magnitudeUnit) {
    case 'Pounds':
      return 'lbs';
    case 'PoundsPerSquareFoot':
      return 'psf';
    case 'PoundsPerLinearFoot':
      return 'plf';
  }
}
